/**
 * KalibLogo — single source of truth for the Kalib AI horizontal lockup.
 *
 * Renders the SVG lockup at a consistent height across every surface
 * (Landing, About, Intelligence app shell, auth screens, Sample Reveal
 * marketing pages, future PDF/Reveal export header).
 *
 * Two variants:
 *   - "light"  → use on cream / white page backgrounds (wordmark in #151515)
 *   - "dark"   → use on dark page backgrounds (wordmark in #FFF7E8)
 *
 * Source SVGs live in /public/brand/kalib-logo-on-{light,dark}.svg.
 * They have been cropped (viewBox="50 75 395 130") to remove the
 * padding from the original 960×260 export so the rendered <img>
 * hugs its content; the visible aspect ratio is now ≈ 3.04:1, and
 * `height` corresponds 1:1 with how tall the visible logo reads.
 *
 * Sizing (per design decision 2026-05-10):
 *   - Standard header / sticky nav:  height={28}    ← default
 *   - Footer / dense:                 height={22}
 *   - Hero / marquee surfaces:        height={40}
 *
 * The component returns just the <img>; pages that need a clickable logo
 * still wrap this in their own <Link to="/"> (preserves their data-testids
 * and interaction patterns).
 */

const SRC = {
  light: "/brand/kalib-logo-on-light.svg",
  dark: "/brand/kalib-logo-on-dark.svg",
};

const NATIVE_WIDTH = 395;
const NATIVE_HEIGHT = 130;
const ASPECT = NATIVE_WIDTH / NATIVE_HEIGHT; // ≈ 3.04

export function KalibLogo({
  variant = "light",
  height = 28,
  className = "",
  alt = "Kalib AI",
  testId = "kalib-logo",
}) {
  const src = SRC[variant] || SRC.light;
  const width = Math.round(height * ASPECT);
  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      className={className}
      style={{ display: "block", height: `${height}px`, width: "auto" }}
      data-testid={testId}
      draggable={false}
    />
  );
}

export default KalibLogo;
